import { Fingerprint, Lock, MapPin, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuoteModal } from "../lib/quoteModal";

// Auto insurers only (verified set) — same scrolling rail as the homepage comparison section.
import { ProviderRail, INSURIFY_AUTO_URL } from "./ProviderRail";


// Iter-105 — Dark trust strip is now PURE platform-credibility metrics.
//
// Iter-106 — Tiles are now tappable trust→education on-ramps. Each tile
// links to a relevant explainer / surface so the dark strip becomes
// active conversion real-estate instead of static decoration:
//
//   120+ Auto insurers       → /compare           (catalog browse)
//   50-state Quote coverage  → /auto-insurance#coverage-map (interactive US map)
//   Soft pull Credit-safe    → /how-we-make-money (transparency one-pager)
//   Bank-level encryption    → /privacy           (data-handling policy)
//
// All 4 are existing routes — no new pages introduced. Click adds a
// `group-hover:border-[#10B981]/60` ring fade + faint glow (matches the
// new iter-106 `.lift-ring` motion token) so the tappable affordance is
// felt without being shouty.
// Iter-422i — insurer tile is a static, insurer-only "120+" (Insurify network);
// lenders never count toward it.
const STATS = [
  {
    Icon: ShieldCheck,
    v: "120",
    suffix: "+",
    l: "Auto insurers",
    to: "/compare",
  },
  {
    Icon: MapPin,
    v: "50",
    suffix: " states + D.C.",
    l: "Nationwide coverage",
    tid: "quote-coverage",
    to: "/auto-insurance#coverage-map",
  },
  {
    // Iter-185.O — Icon swapped from ShieldCheck → Fingerprint to
    // match the new Hero "Soft credit pull" trust pill (iter-185.N).
    // ShieldCheck read as insurance; Fingerprint conveys the identity-
    // check semantic of a soft credit pull more precisely.
    Icon: Fingerprint,
    v: "Soft",
    suffix: " pull",
    l: "Credit-safe pre-qual",
    // Iter-170 — Owner request: this tile should open the lead-capture
    // modal instead of routing to the static `/how-we-make-money` page.
    // The trust signal IS soft-pull pre-qual, and clicking should drop
    // the user into the funnel that delivers it. Sentinel value
    // `openQuoteModal` is handled in the render below.
    to: "__OPEN_QUOTE_MODAL__",
    modalCategory: "auto-loan-new",
  },
  {
    Icon: Lock,
    v: "256-bit",
    suffix: "",
    l: "Secure encryption",
    tid: "bank-level-encryption",
    to: "/privacy",
  },
];

// Iter-145 — Mobile tile spacing fix: the previous inline suffix
// `<span>{suffix}</span>` rendered "50 states + D.C." next to "50" on
// the same baseline, which cramped against the right edge of the 2-col
// mobile grid. Suffix is now visually separated:
//   - Long-form suffixes (>7 chars, like " states + D.C.") block under
//     the value on mobile, inline on desktop.
//   - Short suffixes (+, /yr, " pull", " credit-safe", etc.) stay inline
//     both breakpoints.
//
// Iter-185.B (Sprint B · audit item M3) — Threshold bumped from 4 → 7
// chars. The 4-char rule was too aggressive for the "Soft pull" /
// "credit-safe pre-qual" tile suffixes ("pull" = 4 chars trips the rule
// and wraps "pull" awkwardly under "Soft" on mobile). 7 chars is the
// natural break-point: " states + D.C." (15) and " QUOTE COVERAGE" (16)
// still wrap, but short common suffixes stay inline.
function StatValue({ v, suffix }) {
  if (!suffix) {
    return (
      <div className="font-display font-black text-2xl lg:text-3xl text-white leading-none tracking-tight">
        {v}
      </div>
    );
  }
  const isLong = suffix.trim().length > 7;
  return (
    <div className="font-display font-black text-2xl lg:text-3xl text-white leading-none tracking-tight">
      <span className="whitespace-nowrap">
        {v}
        {!isLong && (
          <span className="text-base lg:text-lg text-[#10B981] font-bold ml-0.5">
            {suffix}
          </span>
        )}
      </span>
      {isLong && (
        <span className="block lg:inline text-[11px] lg:text-lg text-[#10B981] font-bold lg:ml-1 mt-1 lg:mt-0 leading-snug uppercase tracking-[0.06em] lg:tracking-normal lg:normal-case">
          {suffix.trim()}
        </span>
      )}
    </div>
  );
}

export default function TrustBar() {
  // Iter-170 — Soft-pull tile now opens the lead-capture modal instead
  // of routing to the static transparency page.
  const { open: openQuoteModal } = useQuoteModal();
  return (
    <section
      data-testid="trust-bar-section"
      className="relative bg-[#0A2540] overflow-hidden"
    >
      {/* Subtle emerald radial glow — adds depth without noise */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 75% 50%, #10B981 0%, transparent 60%)",
        }}
      />

      {/* Platform-credibility stat row — mobile 2x2 grid, desktop 4-col
          with hairline dividers. No sources pill / methodology row —
          these are product facts, the hero owns the editorial citation
          system. */}
      <div className="relative max-w-wide mx-auto px-5 lg:px-10 py-7 lg:py-0">
        {/* Phase-2 (iter-154) — `items-stretch` + per-tile `min-h-[92px]`
            keep the 2×2 mobile grid visually balanced when the
            "50 states + D.C." tile's long suffix stacks vertically. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-3 lg:gap-0 lg:divide-x lg:divide-white/10 items-stretch">
          {STATS.map(({ Icon, v, suffix, l, tid, to, modalCategory }) => {
            // Iter-170 — Sentinel value `__OPEN_QUOTE_MODAL__` switches
            // the tile from a Link to a modal-opener button. Keeps the
            // grid layout + classes identical so visual rhythm stays.
            const isModalTrigger = to === "__OPEN_QUOTE_MODAL__";
            const TileEl = isModalTrigger ? "button" : Link;
            const tileProps = isModalTrigger
              ? {
                  type: "button",
                  onClick: () => openQuoteModal(modalCategory || "auto-loan-new"),
                }
              : { to };
            return (
              <TileEl
                key={l}
                {...tileProps}
                data-testid={`trust-stat-${tid || l.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative p-3 lg:p-8 min-h-[92px] lg:min-h-0 flex items-start gap-3 lg:gap-4 transition-colors duration-200 ease-out hover:bg-white/[0.025] focus-visible:outline-none focus-visible:bg-white/[0.04] text-left w-full"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#10B981]/0 group-hover:ring-[#10B981]/30 group-focus-visible:ring-[#10B981]/40 transition-[ring-color,box-shadow] duration-200 ease-out"
                />
                <div className="shrink-0 w-9 h-9 lg:w-11 lg:h-11 rounded-md bg-[#10B981]/12 ring-1 ring-[#10B981]/30 flex items-center justify-center transition-colors duration-200 ease-out group-hover:bg-[#10B981]/20 group-hover:ring-[#10B981]/50">
                  <Icon
                    className="w-4 h-4 lg:w-5 lg:h-5 text-[#10B981] transition-transform duration-300 ease-out group-hover:scale-110"
                    strokeWidth={2}
                  />
                </div>
                <div className="min-w-0">
                  <StatValue v={v} suffix={suffix} />
                  <div className="text-[10px] lg:text-xs uppercase tracking-[0.14em] text-slate-500 mt-1.5 lg:mt-2 font-semibold leading-tight transition-colors duration-200 ease-out group-hover:text-slate-300">
                    {l}
                  </div>
                </div>
              </TileEl>
            );
          })}
        </div>
      </div>

      {/* Auto-insurer app tiles — same ProviderAppTile system as the homepage
          comparison section; insurers only (verified set), static row. */}
      <div
        data-testid="trust-bar-marquee"
        className="relative border-t border-white/10 py-4 lg:pt-8 lg:pb-6"
      >
        <div className="max-w-wide mx-auto px-6 lg:px-10">
          <ProviderRail compact href={INSURIFY_AUTO_URL} testId="trust-bar-insurer-rail" />
        </div>
      </div>
    </section>
  );
}
